import React from "react";

/**
 * Reusable InfoTooltip component that displays an info icon with a hover tooltip
 * @param {Object} props
 * @param {React.ReactNode} props.content - The content to display in the tooltip
 * @param {string} props.iconColor - Optional color class for the icon (default: "text-blue-500")
 * @param {string} props.iconSize - Optional size class for the icon (default: "h-5 w-5")
 * @param {string} props.tooltipWidth - Optional width class for the tooltip (default: "max-w-xs sm:max-w-sm md:max-w-md")
 * @param {string} props.tooltipMaxHeight - Optional max height class for the tooltip (default: "max-h-64")
 */
export function InfoTooltip({ 
  content, 
  iconColor = "text-blue-500", 
  iconSize = "h-5 w-5",
  tooltipWidth = "w-80",
  tooltipMaxHeight = "max-h-64",
  inDialog = false
}) {
  const tooltipClass = inDialog ? "tooltip tooltip-dialog-inline cursor-pointer" : "tooltip cursor-pointer";
  
  return (
    <div className={tooltipClass}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`${iconSize} ${iconColor}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <div className={`tooltip-text p-3 bg-gray-900 text-white rounded-lg text-sm leading-snug ${tooltipWidth}`}>
        {content}
      </div>
    </div>
  );
}

/**
 * Pre-configured component for ELD Level explanations
 */
export function ELDLevelInfoTooltip({ inDialog = true }) {
  return (
    <InfoTooltip
      content={
        <>
          <p className="text-base mb-2 font-semibold">ELD Level Explanations:</p>
          <ul className="list-disc list-inside ml-2 space-y-1 text-sm">
            <li><strong>ELD1:</strong> Beginning - Limited receptive and productive English. Understands isolated words/phrases. Needs extensive support.</li>
            <li><strong>ELD2:</strong> Early Intermediate - Understands simple sentences and phrases. Can communicate basic needs with support.</li>
            <li><strong>ELD3:</strong> Intermediate - Understands conversations on familiar topics. Can produce connected sentences and express ideas.</li>
            <li><strong>Above ELD3:</strong> Advanced - Can participate in academic discussions and handle grade-level content with near-native proficiency.</li>
          </ul>
        </>
      }
      tooltipWidth="w-72 sm:w-80"
      tooltipMaxHeight="max-h-56"
      inDialog={inDialog}
    />
  );
}

